import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet } from '../../lib/api.ts'

interface Placement {
  id: number
  title: string
  practice_name: string
  start_date: string
  end_date: string
  location_city: string
  location_county: string
  status: string
}

export default function ApplicationsPage() {
  const [placements, setPlacements] = useState<Placement[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet<{ placements: Placement[]; upcoming_shifts_count: number }>(
          '/locum/placements.php?format=json'
        )
        setPlacements(data.placements)
        setCount(data.upcoming_shifts_count)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load placements.')
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text-900">My Placements</h1>
        <p className="mt-1 text-sm text-text-600">{count} upcoming hired shift{count === 1 ? '' : 's'}</p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Shortlisted and hired work</CardTitle>
        </CardHeader>
        <CardContent>
          {placements.length === 0 ? (
            <p className="text-text-700">No shortlisted or hired placements yet.</p>
          ) : (
            <div className="space-y-3">
              {placements.map((placement) => (
                <div key={placement.id} className="rounded-lg border border-surface-300 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-text-900">{placement.title}</h3>
                      <p className="text-sm text-text-600">{placement.practice_name}</p>
                      <p className="mt-1 text-sm text-text-500">
                        {[placement.location_city, placement.location_county].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <span className="rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold capitalize text-brand-700">
                      {placement.status}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-text-600">
                    {placement.start_date}
                    {placement.end_date && placement.end_date !== placement.start_date ? ` to ${placement.end_date}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
